/**
 * 开票账务明细弹框
 */
import React, { useImperativeHandle, useRef, useState } from 'react';
import { Button, Modal, Table } from 'antd';
import moment from 'moment';
import intl from 'react-intl-universal';
import useInvoicePortal from '@/pages/bill/invoice/index';
import { DATE_TIME_FORMAT } from '@/config';
import { uuid } from '@/utils';
import { parseFen2Currency } from '@/utils/number';
import { getTimeByUtcOffset } from '@/utils/intl';
import { invoiceTypeMap } from '../../constant';
import styles from './index.module.scss';

export interface InvoicedHistoryModalRef {
  openModal: (obj: { invoices: any[]; moreInvoiceFlag: boolean; orders: any[] }) => void;
}

interface IProps {
  innerRef: React.Ref<InvoicedHistoryModalRef>;
  callback?: () => void;
}

const Index: React.FC<IProps> = ($$) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [showComfirmBtn, setShowComfirmBtn] = useState<boolean>(false);
  const [dataSouce, setSataSouce] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);

  const { innerRef } = $$;

  // 开票抽屉
  const invoicePortal = useInvoicePortal();

  const resetData = () => {
    setVisible(false);
    setShowComfirmBtn(false);
    setSataSouce([]);
    setOrders([]);
  };

  // 确定
  const handleSubmitClick = () => {
    // 打开开票抽屉
    invoicePortal.showDrawer({
      isView: false,
      orders,
      callback: $$.callback,
    });
    resetData();
  };

  // 详情
  const detailHandle = (logId: any) => {
    // 打开开票抽屉
    invoicePortal.showDrawer({
      isView: true,
      orders,
      logId,
      callback: $$.callback,
    });
    resetData();
  };

  useImperativeHandle(
    innerRef,
    () => ({
      openModal: (obj: { invoices: any[]; moreInvoiceFlag: boolean; orders: any[] }) => {
        setVisible(true);
        setSataSouce(obj.invoices);
        setShowComfirmBtn(obj.moreInvoiceFlag); // 是否可以继续开票
        setOrders(obj.orders);
      },
    }),
    [],
  );

  const columns: any = [
    {
      title: intl.get('bill_invoice_modal_operate_time'),
      dataIndex: 'createTime',
      key: 'createTime',
      render: (val) => {
        if (!val) return '-';
        return getTimeByUtcOffset(val, DATE_TIME_FORMAT);
      },
    },
    {
      title: intl.get('bill_invoice_modal_invoice_operator'),
      dataIndex: 'createBy',
      key: 'createBy',
      render: (_val, record) => {
        return `${record.createBy || ''}${record.operatorName || ''}`;
      },
    }, // 工号+人名
    {
      title: intl.get('bill_invoice_modal_order_no'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      render: (_val, record) => {
        const { orders: orderList } = record;
        if (!orderList || orderList.length === 0) return '-';
        return orderList.map((item) => <div key={uuid()}>{item?.orderNo}</div>);
      },
    },
    {
      title: intl.get('bill_invoice_modal_available_amt'),
      dataIndex: 'availableAmt',
      key: 'availableAmt',
      render: (_val, record) => {
        const { orders: orderList } = record;
        if (!orderList || orderList.length === 0) return '-';
        return orderList.map((item) => (
          <div key={uuid()}>{parseFen2Currency(item?.availableAmt)}</div>
        ));
      },
    },
    {
      title: intl.get('bill_invoice_modal_invoiced_amt'),
      dataIndex: 'invoicedAmt',
      key: 'invoicedAmt',
      render: (_val, record) => {
        const { orders: orderList } = record;
        if (!orderList || orderList.length === 0) return '-';
        return orderList.map((item) => (
          <div key={uuid()}>{parseFen2Currency(item?.invoicedAmt)}</div>
        ));
      },
    },
    {
      title: intl.get('bill_invoice_modal_invoice_code'),
      dataIndex: 'invoiceCode',
      key: 'invoiceCode',
      render: (_val, record) => {
        const { invoices } = record;
        if (!invoices || invoices.length === 0) return '-';
        return invoices.map((item) => <div key={uuid()}>{item?.invoiceCode}</div>);
      },
    },
    {
      title: intl.get('bill_invoice_modal_invoice_no'),
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      render: (_val, record) => {
        const { invoices } = record;
        if (!invoices || invoices.length === 0) return '-';
        return invoices.map((item) => <div key={uuid()}>{item?.invoiceNo}</div>);
      },
    },
    {
      title: intl.get('bill_invoice_modal_invoice_type'),
      dataIndex: 'invoiceType',
      key: 'invoiceType',
      render: (_val, record) => {
        const { invoices } = record;
        if (!invoices || invoices.length === 0) return '-';
        return invoices.map((item) => <div key={uuid()}>{invoiceTypeMap[item?.invoiceType]}</div>);
      },
    },
    {
      title: intl.get('bill_invoice_modal_invoice_amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (_val, record) => {
        const { invoices } = record;
        if (!invoices || invoices.length === 0) return '-';
        return invoices.map((item) => <div key={uuid()}>{parseFen2Currency(item?.amount)}</div>);
      },
    },
    {
      title: intl.get('bill_invoice_modal_operate'),
      dataIndex: 'operate',
      key: 'operate',
      align: 'right',
      render: (_val, record) => (
        <Button type="link" onClick={() => detailHandle(record?.id)}>
          {intl.get('bill_invoice_modal_details')}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={intl.get('bill_invoice_modal_invoice_detail')}
        width={1400}
        wrapClassName={styles.InvoicedHistoryModal}
        open={visible}
        onCancel={resetData}
        footer={
          <>
            {showComfirmBtn ? (
              <Button type="primary" onClick={handleSubmitClick} className={styles.submitBtn}>
                {intl.get('bill_invoice_modal_confirm')}
              </Button>
            ) : null}
            <Button type="default" onClick={resetData} className={styles.submitBtn}>
              {intl.get('app_confirm_cancel_btn')}
            </Button>
          </>
        }
      >
        <div className={styles.title}>
          {showComfirmBtn
            ? intl.get('bill_invoice_modal_invoice_history_title1')
            : intl.get('bill_invoice_modal_invoice_history_title2')}
        </div>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={dataSouce || []}
          scroll={{ x: 'max-content' }}
          className={styles.roomListTable}
          pagination={false}
        />
      </Modal>
    </>
  );
};

export default Index;
